
import router from "@/router"
import { Component, Vue } from "vue-property-decorator"
import { filter } from "vue/types/umd"
import SearchQuery from "../graphql/search.graphql"
import GroupsQuery from "@/graphql/group/groups.graphql"
import eventbus from "@/eventbus"

const maxResults = 100
const maxRecentQueries = 5
const maxRecentlyVisited = 5
const minQueryLength = 2

interface SearchResult {
    type: string
    uuid: string
    title: string
    description?: string
    location?: string
    state?: string
    no?: string
    avatarUrl?: string
    date?: Date
    groupUuid?: string
}

@Component({
    apollo: {
        search: {
            query: SearchQuery,
            variables() {
                return { query: this.query }
            },
            skip() {
                return !this.searchReady
            },
            update(data) {
                this.searching = false
                this.results = data.search
                if (this.results.length > maxResults) {
                    console.warn(`Search got ${this.results.length} results.. limiting to ${maxResults}`)
                    this.results.splice(maxResults)
                }
                sessionStorage.searchResults = JSON.stringify(this.results)
            },
        },
        groups: GroupsQuery,
    },
})
export default class Search extends Vue {
    query = ""
    searchInputTimeout: any = null
    results: SearchResult[] = []
    groups?: any[]
    searchReady = false
    searching = false
    recentQueries: string[] = []
    recentlyVisited: SearchResult[] = []

    filters = [
        { type: "all" , label: this.$i18n.t("all") },
        { type: "job", label: this.$i18n.t("job") },
        { type: "workreport", label: this.$i18n.t("workReport") },
        // { type: "order", label: this.$i18n.t("jobOrder") }, // Add this when we have order view
        { type: "customer", label: this.$i18n.t("customer") },
        { type: "asset", label: this.$i18n.t("asset") },
        { type: "user", label: this.$i18n.t("user") },
        // { type: "group", label: this.$i18n.t("group") }, // Add this when we have group view
    ]
    activeFilter: any = {}

    get filteredResults() {
        if (!this.query) return this.results // cuz it's the recently visited
        return this.results.filter((result: SearchResult) => {
            return !this.activeFilter.type || this.activeFilter.type == "all" || result.type == this.activeFilter.type
        })
    }

    mounted() {
        this.recentQueries = localStorage.searchRecentQueries ? JSON.parse(localStorage.searchRecentQueries) : []
        this.recentlyVisited = localStorage.searchRecentlyVisited ? JSON.parse(localStorage.searchRecentlyVisited) : []
        if ("q" in this.$route.params) {
            console.log("search from params")
            this.query = this.$route.params.q
            this.search()
        } else if ("searchResults" in sessionStorage) {
            console.log("search from sessionStorage")
            this.query = sessionStorage.searchQuery
            this.$nextTick(() => {
                this.results = JSON.parse(sessionStorage.searchResults)
                if (sessionStorage.searchFilter) this.activeFilter = JSON.parse(sessionStorage.searchFilter)
                if (sessionStorage.searchResultScroll) {
                    this.$nextTick(() => {
                        document.body.scrollTop = sessionStorage.searchResultScroll
                    })
                }
            })
        } else {
            this.results = this.recentlyVisited
            this.activeFilter = {}
        }
        this.$nextTick(() => this.resize())
        eventbus.$on('search-tab-clicked', this.focusSearchField);
    }

    beforeDestroy() {
        eventbus.$off('search-tab-clicked', this.focusSearchField);
    }

    focusSearchField() {
        const searchField = this.$refs.search as HTMLInputElement;
        searchField.focus();
    }

    updated() {
        this.resize()
    }

    to(result: SearchResult) {
        sessionStorage.searchResultScroll = document.body.scrollTop
        sessionStorage.searchFilter = JSON.stringify(this.activeFilter)
        this.addRecentlyVisited(result)
        router.push(`/${result.type}/${result.uuid}`)
    }

    icon(result: SearchResult) {
        switch (result.type) {
            case "job":
                return "work"
            case "order":
                return "work_outline"
            case "workreport":
                return "done"
            case "user":
                return "person"
            case "group":
                return "group"
            case "customer":
                return "contact_mail"
            case "asset":
                return "domain"
            default:
                return ""
        }
    }

    state(result: SearchResult) {
        if (!result || !result.state) return ""
        let type: string = result.type
        let state: string | undefined = result.state
        switch (type) {
            case "job":
                return this.$i18n.t(`jobState_${state}`)
            case "order":
                return this.$i18n.t(`jobOrderState_${state}`)
            case "asset":
                return this.$i18n.t(`assetState_${state}`)
            default:
                return ""
        }
    }

    formatNo(result: SearchResult) {
        if ((result.type == "job" || result.type == "workreport") && result.no && result.groupUuid && this.groups) {
            const group = this.groups.find((g) => g.uuid == result.groupUuid || g.id == result.groupUuid)
            if (group && group.prefix) return `${group.prefix}${result.no}`
        }
        return result.no
    }

    searchRecent(input: string) {
        // Use a previous search term as the query
        if (!input) return
        this.query = input
        this.search()
    }

    searchInput() {
        this.searchReady = false
        delete sessionStorage.searchQuery
        delete sessionStorage.searchResults
        delete sessionStorage.searchFilter
        delete sessionStorage.searchResultScroll
        this.results = []
        if (this.searchInputTimeout) clearTimeout(this.searchInputTimeout)
        this.searchInputTimeout = setTimeout(() => {
            this.search()
        }, 500)
    }

    search() {
        if (this.searchInputTimeout) clearTimeout(this.searchInputTimeout)
        this.searchInputTimeout = null
        if (!this.query) {
            this.results = this.recentlyVisited
            this.activeFilter = {}
        } else if (this.query.length >= minQueryLength) {
            this.searchReady = true
            this.searching = true
            sessionStorage.searchQuery = this.query
            this.addRecentQuery(this.query)
        }
    }

    addRecentQuery(query: string) {
        const index = this.recentQueries.indexOf(query)
        if (index == 0) return // already at the top
        if (index < 0) {
            // not in the list - add it at the top
            this.recentQueries.unshift(query)
            if (this.recentQueries.length > maxRecentQueries) this.recentQueries.splice(maxRecentQueries)
        } else {
            // already in the list - move it to the top
            this.recentQueries.splice(index, 1)
            this.recentQueries.unshift(query)
        }
        localStorage.searchRecentQueries = JSON.stringify(this.recentQueries)
    }

    addRecentlyVisited(result: SearchResult) {
        const index = this.recentlyVisited.findIndex((r) => r.type == result.type && r.uuid == result.uuid)
        if (index == 0) return // already at the top
        if (index < 0) {
            // not in the list - add it at the top
            this.recentlyVisited.unshift(result)
            if (this.recentlyVisited.length > maxRecentlyVisited) this.recentlyVisited.splice(maxRecentlyVisited)
        } else {
            // already in the list - move it to the top
            this.recentlyVisited.splice(index, 1)
            this.recentlyVisited.unshift(result)
        }
        localStorage.searchRecentlyVisited = JSON.stringify(this.recentlyVisited)
    }

    clearRecentQueries() {
        this.recentQueries = []
        delete localStorage.searchRecentQueries
    }

    clearRecentlyVisited() {
        if (!this.query && this.results.length == this.recentlyVisited.length) this.results = []
        this.recentlyVisited = []
        delete localStorage.searchRecentlyVisited
    }

    resize() {
        const bar = (this.$refs.searchbar as Vue).$el as HTMLElement
        if (bar && bar.parentElement) {
            bar.style.width = `${bar.parentElement.clientWidth}px`
        }
    }

    blurSearchField() {
        const field = (this.$refs.search as Vue).$el as HTMLElement
        if (field) {
            field.blur()
        }
    }

    searchFieldSumbit() {
        this.blurSearchField()
        if (this.query == sessionStorage.searchQuery) return // Avoid searching for the same thing twice
        this.search()
    }
}
